import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, Popup, useMapEvents, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

/* ── types ─────────────────────────────────────────────── */
interface Coordenada { lat: number; lng: number; }

interface Tuberia {
  id: string;
  nombre: string;
  descripcion?: string;
  tipo: string;
  material: string;
  diametro?: string;
  estado: string;
  coordenadas: string;
  instaladoPor?: string;
  fechaInstalacion?: string;
  createdAt: string;
}

const ESTADO_COLOR: Record<string, string> = {
  activo:   '#22C55E',
  dañado:   '#EF4444',
  inactivo: '#64748B',
};

const ESTADO_DASH: Record<string, string | undefined> = {
  activo:   undefined,
  dañado:   undefined,
  inactivo: '5,5',
};

const ESTADO_WEIGHT: Record<string, number> = {
  activo:   4,
  dañado:   4,
  inactivo: 3,
};

const TIPO_LABEL: Record<string, string> = {
  principal:  'Principal',
  secundario: 'Secundario',
  conexion:   'Conexion',
};

const ESTADO_LABEL: Record<string, string> = {
  activo:   'Activo',
  dañado:   'Danado',
  inactivo: 'Inactivo',
};

/* ── helpers ────────────────────────────────────────────── */
const parseCoords = (raw: string): Coordenada[] => {
  try { return JSON.parse(raw) ?? []; } catch { return []; }
};

const toLatLng = (coords: Coordenada[]): [number, number][] =>
  coords.map(c => [c.lat, c.lng]);

/* ── MapClickHandler sub-component ─────────────────────── */
interface MapClickHandlerProps {
  active: boolean;
  onMapClick: (lat: number, lng: number) => void;
  onMapDblClick: () => void;
}
const MapClickHandler: React.FC<MapClickHandlerProps> = ({ active, onMapClick, onMapDblClick }) => {
  useMapEvents({
    click(e) {
      if (active) onMapClick(e.latlng.lat, e.latlng.lng);
    },
    dblclick(e) {
      if (active) {
        e.originalEvent.preventDefault();
        onMapDblClick();
      }
    },
  });
  return null;
};

/* ── form initial state ─────────────────────────────────── */
const FORM_INIT = { nombre: '', tipo: 'principal', material: 'PVC', diametro: '', estado: 'activo' };

/* ── main component ─────────────────────────────────────── */
const Tuberias: React.FC = () => {
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  const isGerente = usuario.rol === 'gerente';

  const [tuberias, setTuberias]     = useState<Tuberia[]>([]);
  const [loading, setLoading]       = useState(true);
  const [filtro, setFiltro]         = useState<string>('todos');

  /* drawing state */
  const [drawMode, setDrawMode]     = useState(false);
  const [drawPoints, setDrawPoints] = useState<Coordenada[]>([]);

  /* form state */
  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [pendingCoords, setPendingCoords] = useState<Coordenada[]>([]);
  const [form, setForm]             = useState({ ...FORM_INIT });
  const [saving, setSaving]         = useState(false);

  /* selected tuberia for popup highlight */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /* ── data load ── */
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tuberias');
      setTuberias(res.data);
    } catch { /* silenciar */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  /* ── drawing ── */
  const handleMapClick = (lat: number, lng: number) => {
    setDrawPoints(prev => [...prev, { lat, lng }]);
  };

  const handleMapDblClick = () => {
    if (drawPoints.length >= 2) finishDrawing();
  };

  const finishDrawing = () => {
    if (drawPoints.length < 2) { alert('Agrega al menos 2 puntos para crear un tramo'); return; }
    setPendingCoords(drawPoints);
    setDrawMode(false);
    setDrawPoints([]);
    setEditingId(null);
    setForm({ ...FORM_INIT });
    setShowForm(true);
  };

  const cancelDrawing = () => {
    setDrawMode(false);
    setDrawPoints([]);
  };

  /* ── edit ── */
  const abrirEdicion = (t: Tuberia) => {
    setEditingId(t.id);
    setForm({ nombre: t.nombre, tipo: t.tipo, material: t.material, diametro: t.diametro ?? '', estado: t.estado });
    setPendingCoords(parseCoords(t.coordenadas));
    setShowForm(true);
  };

  const cancelarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setPendingCoords([]);
    setForm({ ...FORM_INIT });
  };

  /* ── save ── */
  const guardar = async () => {
    if (!form.nombre.trim()) { alert('El nombre es obligatorio'); return; }
    if (pendingCoords.length < 2 && !editingId) { alert('Necesitas al menos 2 puntos en el mapa'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        coordenadas: JSON.stringify(pendingCoords),
      };
      if (editingId) {
        await api.put(`/tuberias/${editingId}`, payload);
      } else {
        await api.post('/tuberias', payload);
      }
      cancelarForm();
      cargar();
    } catch { alert('Error al guardar el tramo'); }
    finally { setSaving(false); }
  };

  /* ── delete ── */
  const eliminar = async (id: string) => {
    if (!window.confirm('Eliminar este tramo?')) return;
    try {
      await api.delete(`/tuberias/${id}`);
      cargar();
    } catch { alert('Error al eliminar'); }
  };

  /* ── filtered list ── */
  const tuberiasFiltradas = filtro === 'todos' ? tuberias : tuberias.filter(t => t.estado === filtro);

  /* ── stats ── */
  const total    = tuberias.length;
  const activos  = tuberias.filter(t => t.estado === 'activo').length;
  const danados  = tuberias.filter(t => t.estado === 'dañado').length;
  const inactivos = tuberias.filter(t => t.estado === 'inactivo').length;

  /* ── styles ── */
  const inputStyle: React.CSSProperties = {
    width: '100%', background: '#0f172a', border: '1px solid #334155',
    color: '#e2e8f0', borderRadius: 8, padding: '8px 12px',
    marginBottom: 12, boxSizing: 'border-box', fontSize: 14,
  };

  const labelStyle: React.CSSProperties = {
    color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 4,
  };

  const filterBtn = (val: string, label: string, color: string) => (
    <button
      key={val}
      onClick={() => setFiltro(val)}
      style={{
        background: filtro === val ? color + '33' : 'transparent',
        border: `1px solid ${filtro === val ? color : '#334155'}`,
        color: filtro === val ? color : '#64748b',
        borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 48px)', overflow: 'hidden' }}>

      {/* ── Left sidebar ── */}
      <div style={{
        width: 300, minWidth: 300, background: '#0f172a', borderRight: '1px solid #1e293b',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: '18px 16px 12px', borderBottom: '1px solid #1e293b' }}>
          <h2 style={{ color: '#f1f5f9', margin: '0 0 4px', fontSize: 18, fontWeight: 800 }}>
            Tuberias
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: 12 }}>
            Red de distribucion de agua
          </p>
        </div>

        {showForm ? (
          /* ── Add / Edit form ── */
          <div style={{ padding: 16, flex: 1, overflowY: 'auto' }}>
            <h3 style={{ color: '#f1f5f9', margin: '0 0 16px', fontSize: 15 }}>
              {editingId ? 'Editar tramo' : 'Nuevo tramo'}
            </h3>
            {!editingId && pendingCoords.length > 0 && (
              <div style={{ background: '#22c55e22', border: '1px solid #22c55e44', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#22c55e', fontSize: 12 }}>
                {pendingCoords.length} puntos trazados en el mapa
              </div>
            )}

            <label style={labelStyle}>Nombre *</label>
            <input
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Ej: Tramo Av. Principal"
              style={inputStyle}
            />

            <label style={labelStyle}>Tipo</label>
            <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={inputStyle}>
              <option value="principal">Principal</option>
              <option value="secundario">Secundario</option>
              <option value="conexion">Conexion</option>
            </select>

            <label style={labelStyle}>Material</label>
            <select value={form.material} onChange={e => setForm(f => ({ ...f, material: e.target.value }))} style={inputStyle}>
              <option value="PVC">PVC</option>
              <option value="hierro">Hierro</option>
              <option value="HDPE">HDPE</option>
              <option value="otro">Otro</option>
            </select>

            <label style={labelStyle}>Diametro</label>
            <input
              value={form.diametro}
              onChange={e => setForm(f => ({ ...f, diametro: e.target.value }))}
              placeholder="Ej: 2 pulgadas"
              style={inputStyle}
            />

            <label style={labelStyle}>Estado</label>
            <select value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))} style={inputStyle}>
              <option value="activo">Activo</option>
              <option value="dañado">Danado</option>
              <option value="inactivo">Inactivo</option>
            </select>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                onClick={cancelarForm}
                style={{ flex: 1, background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '8px 0', cursor: 'pointer', fontSize: 13 }}
              >
                Cancelar
              </button>
              <button
                onClick={guardar}
                disabled={saving}
                style={{ flex: 1, background: '#f59e0b', border: 'none', color: '#0f172a', borderRadius: 8, padding: '8px 0', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          /* ── Normal sidebar content ── */
          <>
            {/* Stats */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e293b' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Total',    value: total,    color: '#38bdf8' },
                  { label: 'Activos',  value: activos,  color: '#22c55e' },
                  { label: 'Danados',  value: danados,  color: '#ef4444' },
                  { label: 'Inactivos',value: inactivos, color: '#64748b' },
                ].map(s => (
                  <div key={s.label} style={{ background: '#1e293b', borderRadius: 8, padding: '8px 10px', border: `1px solid ${s.color}33` }}>
                    <div style={{ color: '#64748b', fontSize: 11 }}>{s.label}</div>
                    <div style={{ color: s.color, fontSize: 20, fontWeight: 800 }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Add button */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e293b' }}>
              <button
                onClick={() => { setDrawMode(true); setDrawPoints([]); }}
                disabled={drawMode}
                style={{
                  width: '100%', background: drawMode ? '#334155' : '#f59e0b',
                  border: 'none', color: drawMode ? '#64748b' : '#0f172a',
                  borderRadius: 8, padding: '10px 0', cursor: drawMode ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: 14,
                }}
              >
                {drawMode ? 'Modo dibujo activo...' : '+ Agregar tramo'}
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e293b', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {filterBtn('todos',    'Todos',    '#38bdf8')}
              {filterBtn('activo',   'Activo',   '#22c55e')}
              {filterBtn('dañado',   'Danado',   '#ef4444')}
              {filterBtn('inactivo', 'Inactivo', '#64748b')}
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {loading ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 32, fontSize: 13 }}>Cargando...</div>
              ) : tuberiasFiltradas.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: 32, fontSize: 13 }}>No hay tramos registrados</div>
              ) : (
                tuberiasFiltradas.map(t => {
                  const color = ESTADO_COLOR[t.estado] ?? '#64748b';
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedId(t.id === selectedId ? null : t.id)}
                      style={{
                        padding: '10px 16px', borderBottom: '1px solid #1e293b', cursor: 'pointer',
                        background: selectedId === t.id ? '#1e293b' : 'transparent',
                        borderLeft: selectedId === t.id ? `3px solid ${color}` : '3px solid transparent',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.nombre}
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                            <span style={{ background: '#1e293b', color: '#94a3b8', borderRadius: 4, padding: '1px 7px', fontSize: 11 }}>
                              {TIPO_LABEL[t.tipo] ?? t.tipo}
                            </span>
                            <span style={{ background: color + '22', color, borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                              {ESTADO_LABEL[t.estado] ?? t.estado}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginLeft: 8, flexShrink: 0 }}>
                          <button
                            onClick={e => { e.stopPropagation(); abrirEdicion(t); }}
                            title="Editar"
                            style={{ background: '#0ea5e922', border: '1px solid #0ea5e944', color: '#38bdf8', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}
                          >
                            Editar
                          </button>
                          {isGerente && (
                            <button
                              onClick={e => { e.stopPropagation(); eliminar(t.id); }}
                              title="Eliminar"
                              style={{ background: '#ef444422', border: '1px solid #ef444444', color: '#ef4444', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}
                            >
                              X
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Map ── */}
      <div style={{ flex: 1, position: 'relative' }}>
        {/* Drawing instruction chip */}
        {drawMode && (
          <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            background: '#0f172aee', border: '1px solid #f59e0b', borderRadius: 10,
            padding: '10px 18px', zIndex: 1000, color: '#f59e0b', fontSize: 13, fontWeight: 600,
            textAlign: 'center', pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>
            Haz clic en el mapa para agregar puntos. Doble clic o boton &quot;Guardar&quot; para terminar.
            {drawPoints.length > 0 && <span style={{ color: '#38bdf8', marginLeft: 8 }}>{drawPoints.length} puntos</span>}
          </div>
        )}

        {/* Drawing action buttons */}
        {drawMode && (
          <div style={{
            position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 10, zIndex: 1000,
          }}>
            <button
              onClick={cancelDrawing}
              style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              Cancelar
            </button>
            <button
              onClick={finishDrawing}
              disabled={drawPoints.length < 2}
              style={{
                background: drawPoints.length >= 2 ? '#f59e0b' : '#334155',
                border: 'none', color: drawPoints.length >= 2 ? '#0f172a' : '#64748b',
                borderRadius: 8, padding: '9px 20px', cursor: drawPoints.length >= 2 ? 'pointer' : 'not-allowed',
                fontWeight: 700, fontSize: 14,
              }}
            >
              Guardar tramo
            </button>
          </div>
        )}

        <MapContainer
          center={[-12.046374, -77.042793]}
          zoom={13}
          style={{ width: '100%', height: '100%' }}
          doubleClickZoom={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          <MapClickHandler
            active={drawMode}
            onMapClick={handleMapClick}
            onMapDblClick={handleMapDblClick}
          />

          {/* Existing tuberias */}
          {tuberias.map(t => {
            const coords = parseCoords(t.coordenadas);
            if (coords.length < 2) return null;
            const color  = ESTADO_COLOR[t.estado] ?? '#64748b';
            const weight = ESTADO_WEIGHT[t.estado] ?? 3;
            const dash   = ESTADO_DASH[t.estado];
            return (
              <Polyline
                key={t.id}
                positions={toLatLng(coords)}
                pathOptions={{ color, weight, dashArray: dash, opacity: selectedId && selectedId !== t.id ? 0.35 : 1 }}
              >
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{t.nombre}</div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 2 }}>Tipo: {TIPO_LABEL[t.tipo] ?? t.tipo}</div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 2 }}>Material: {t.material}</div>
                    {t.diametro && <div style={{ fontSize: 12, color: '#555', marginBottom: 2 }}>Diametro: {t.diametro}</div>}
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>Estado: {ESTADO_LABEL[t.estado] ?? t.estado}</div>
                    <button
                      onClick={() => abrirEdicion(t)}
                      style={{ background: '#f59e0b', border: 'none', color: '#0f172a', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
                    >
                      Editar
                    </button>
                  </div>
                </Popup>
              </Polyline>
            );
          })}

          {/* Drawing: in-progress points */}
          {drawPoints.map((pt, i) => (
            <CircleMarker
              key={i}
              center={[pt.lat, pt.lng]}
              radius={5}
              pathOptions={{ color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 1 }}
            />
          ))}

          {/* Drawing: in-progress polyline */}
          {drawPoints.length >= 2 && (
            <Polyline
              positions={toLatLng(drawPoints)}
              pathOptions={{ color: '#ef4444', weight: 3, dashArray: '6,4' }}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default Tuberias;
